/* eslint-disable react-refresh/only-export-components */
import { type ReactElement } from 'react';
import {
  TableV2ActionsHeaderIcon,
  TableV2HeaderLabel,
  TableV2SortableHeader,
  tableV2ActionsColumnWidth,
  tableV2ChevronColumnWidth,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import { s } from '@/theme/tokens';
import { TableCell, TableRow } from '@/ui/primitives';
import type { TableV2SkeletonColumnKind } from '@/features/shared/components/tableSystem/TableV2Skeleton';
import type { ApiSort } from '@/ui/composites/grid/gridUtils';

export type SortablePipelineRunField = 'buildKey' | 'startedAt' | 'status';
export type SortableStageRunField = 'buildKey' | 'startedAt' | 'status';
export type SortableJobRunField = 'jobName' | 'startedAt' | 'status';

export const nestedRunSkeletonColumns = [
  'chevron',
  'name',
  'status',
  'passRate',
  'duration',
  'dateTime',
  'tags',
  'actions',
] satisfies TableV2SkeletonColumnKind[];

export const nestedStageRunSkeletonColumns = [
  'chevron',
  'name',
  'status',
  'passRate',
  'duration',
  'dateTime',
  'tags',
  'actions',
] satisfies TableV2SkeletonColumnKind[];

export const nestedJobRunSkeletonColumns = [
  'name',
  'status',
  'duration',
  'dateTime',
  'tags',
  'actions',
] satisfies TableV2SkeletonColumnKind[];

export const buildPipelineRunColgroup = (): ReactElement => (
  <colgroup>
    <col width={tableV2ChevronColumnWidth} />
    <col width="21%" />
    <col width="11%" />
    <col width="15%" />
    <col width="11%" />
    <col width="20%" />
    <col width="16%" />
    <col width={tableV2ActionsColumnWidth} />
  </colgroup>
);

export const buildStageRunColgroup = (): ReactElement => (
  <colgroup>
    <col width={tableV2ChevronColumnWidth} />
    <col width="21%" />
    <col width="11%" />
    <col width="15%" />
    <col width="11%" />
    <col width="20%" />
    <col width="16%" />
    <col width={tableV2ActionsColumnWidth} />
  </colgroup>
);

export const buildJobRunColgroup = (): ReactElement => (
  <colgroup>
    <col width="24%" />
    <col width="13%" />
    <col width="16%" />
    <col width="24%" />
    <col width="19%" />
    <col width={tableV2ActionsColumnWidth} />
  </colgroup>
);

export const PipelineNestedHeader = ({
  sort,
  onSortChange,
}: {
  sort: ApiSort;
  onSortChange: (field: SortablePipelineRunField) => void;
}): ReactElement => (
  <TableRow>
    <TableCell sx={{ p: s('none'), width: tableV2ChevronColumnWidth }} />
    <TableCell>
      <TableV2SortableHeader
        activeSort={sort}
        field="buildKey"
        label="Run"
        onSortChange={onSortChange}
      />
    </TableCell>
    <TableCell>
      <TableV2SortableHeader
        activeSort={sort}
        field="status"
        label="Status"
        onSortChange={onSortChange}
      />
    </TableCell>
    <TableCell>
      <TableV2HeaderLabel label="Pass Rate" />
    </TableCell>
    <TableCell>
      <TableV2HeaderLabel label="Duration" />
    </TableCell>
    <TableCell>
      <TableV2SortableHeader
        activeSort={sort}
        field="startedAt"
        label="Ran At"
        onSortChange={onSortChange}
      />
    </TableCell>
    <TableCell>
      <TableV2HeaderLabel label="Tags" />
    </TableCell>
    <TableCell align="center">
      <TableV2ActionsHeaderIcon />
    </TableCell>
  </TableRow>
);

export const StageRunNestedHeader = ({
  nameHeaderLabel = 'Stage Run',
  sort,
  onSortChange,
}: {
  nameHeaderLabel?: string;
  sort: ApiSort;
  onSortChange: (field: SortableStageRunField) => void;
}): ReactElement => (
  <TableRow>
    <TableCell sx={{ p: s('none'), width: tableV2ChevronColumnWidth }} />
    <TableCell>
      <TableV2SortableHeader
        activeSort={sort}
        field="buildKey"
        label={nameHeaderLabel}
        onSortChange={onSortChange}
      />
    </TableCell>
    <TableCell>
      <TableV2SortableHeader
        activeSort={sort}
        field="status"
        label="Status"
        onSortChange={onSortChange}
      />
    </TableCell>
    <TableCell>
      <TableV2HeaderLabel label="Pass Rate" />
    </TableCell>
    <TableCell>
      <TableV2HeaderLabel label="Duration" />
    </TableCell>
    <TableCell>
      <TableV2SortableHeader
        activeSort={sort}
        field="startedAt"
        label="Ran At"
        onSortChange={onSortChange}
      />
    </TableCell>
    <TableCell>
      <TableV2HeaderLabel label="Tags" />
    </TableCell>
    <TableCell align="center">
      <TableV2ActionsHeaderIcon />
    </TableCell>
  </TableRow>
);

export const JobRunNestedHeader = ({
  nameHeaderLabel = 'Job Run',
  sort,
  onSortChange,
}: {
  nameHeaderLabel?: string;
  sort: ApiSort;
  onSortChange: (field: SortableJobRunField) => void;
}): ReactElement => (
  <TableRow>
    <TableCell>
      <TableV2SortableHeader
        activeSort={sort}
        field="jobName"
        label={nameHeaderLabel}
        onSortChange={onSortChange}
      />
    </TableCell>
    <TableCell>
      <TableV2SortableHeader
        activeSort={sort}
        field="status"
        label="Status"
        onSortChange={onSortChange}
      />
    </TableCell>
    <TableCell>
      <TableV2HeaderLabel label="Duration" />
    </TableCell>
    <TableCell>
      <TableV2SortableHeader
        activeSort={sort}
        field="startedAt"
        label="Ran At"
        onSortChange={onSortChange}
      />
    </TableCell>
    <TableCell>
      <TableV2HeaderLabel label="Tags" />
    </TableCell>
    <TableCell align="center">
      <TableV2ActionsHeaderIcon />
    </TableCell>
  </TableRow>
);
